import { Button, Chip, Tooltip } from '@mui/material';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import Box from '@mui/system/Box';
import axios from 'axios';
import dayjs from 'dayjs';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { getAbsoluteFileUrl, normalizeFIO } from 'src/app/helpers/utils';
import { useStaffByIdMutation } from 'src/app/services/staffApi';

import handleError from '@fuse/core/errorHandler';
import FuseSvgIcon from '@fuse/core/FuseSvgIcon';
import { API } from 'app/configs/api';
import StaffAvatar from 'app/shared-components/StaffAvatar';

import { selectUser } from 'app/store/userSlice';
import { useSelector } from 'react-redux';
import { useUser } from '../hooks/useUser';
import { changeFloorOrder } from '../main/apps/map/store/floorsSlice';
import { userStatusState } from '../main/hr/users/UsersTable';
import { useGetDepartmentByDepartmentIdQuery } from '../services/deparmentsApi';
import StaffContactDetailsSkeleton from './StaffContactDetailsSkeleton';
import StaffOnline from './StaffOnline';

const StaffContactDetails = ({ id }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation('translations');
  const { isTop } = useUser();
  const [profile, setProfile] = useState(null);
  const [getStaff] = useStaffByIdMutation();
  const { data: locationDepartment } = useGetDepartmentByDepartmentIdQuery(
    { id: profile?.placeOfWork?.departmentId },
    { skip: !profile?.placeOfWork?.departmentId }
  );
  const navigate = useNavigate();
  const buildId = locationDepartment?.builds[0]?.id;
  const user = useSelector(selectUser);

  const isHr = user.roles.includes('hr');

  useEffect(() => {
    getStaffById();
  }, [id]);

  function handleDispatchFloor() {
    dispatch(
      changeFloorOrder({
        buildingId: locationDepartment?.builds[0].id,
        floorOrder: locationDepartment?.floors[0].name,
        floorId: locationDepartment?.floors[0].id,
        floorImage: locationDepartment?.floors[0].picture,
        departmentId: locationDepartment?.departmentId,
      })
    );
  }

  function getStaffById() {
    if (id) getStaff({ id }).unwrap().then(setProfile).catch(handleError);
  }

  function renderStatus() {
    const label = profile ? userStatusState.find((s) => s.id === profile?.status)?.title : '';
    switch (profile?.status) {
      case 1:
        return <Chip label={label} color="info" />;
      case 2:
        return <Chip label={label} color="secondary" />;
      case 3:
        return <Chip label={label} color="success" />;
      case 4:
        return <Chip label={label} color="warning" />;
      default:
        return null;
    }
  }

  function navigateToProject(project) {
    if (isTop && project.id) {
      navigate(`/apps/projects/${project.id}/board`);
    }
  }

  function navigateToChat() {
    if (!profile?.applicationUserId || !profile?.id) {
      return;
    }
    axios.get(`${API.getChatId}?userId=${profile?.applicationUserId}`).then((res) => {
      const chatId = res?.data > 0 ? res?.data : 's' + profile?.id;
      navigate(`/apps/chat/${chatId}`);
    });
  }

  function navigateToWorkload() {
    if (profile?.id) {
      const from = dayjs().add(-30, 'd').format('YYYY-MM-DD');
      const to = dayjs().format('YYYY-MM-DD');
      navigate(`/apps/skud/workload/${profile?.id}?from=${from}&to=${to}`);
    }
  }

  function navigateToDocument() {
    navigate(
      `/apps/local-acts-control/${id}?name=${profile?.fullName?.fio
        .split(' ')
        .join('_')
        .toLowerCase()}`
    );
  }

  function navigateToAttendances() {
    if (profile?.id) {
      navigate(`/apps/skud/employees/${profile?.id}`);
    }
  }

  if (!profile) {
    return <StaffContactDetailsSkeleton />;
  }

  return (
    <div className="w-full max-w-640">
      <Box
        className="relative w-full px-32 h-96 sm:px-48"
        sx={{
          backgroundColor: 'background.default',
        }}
      ></Box>
      <div className="relative flex flex-col items-center flex-auto p-24 pt-0 sm:p-48 sm:pt-0 dark:bg-dark">
        <div className="w-50">
          <div className="flex items-end -mt-64">
            <StaffAvatar type="doc" staff={profile} className="font-bold w-128 h-128 text-64" />
            <div className="ml-20">
              <StaffOnline staffId={+id} />
            </div>
            <div className="ml-20">{renderStatus()}</div>
          </div>
          <Typography className="mt-12 mb-5 text-4xl">
            {profile?.fullName ? normalizeFIO(profile?.fullName?.fio) : ''}
          </Typography>
          <div className="flex flex-col gap-16">
            {profile?.placeOfWork?.topName && (
              <div className="flex items-center">
                <Tooltip title={t('TopManager')} arrow placement="top">
                  <FuseSvgIcon>react-outline:briefcase</FuseSvgIcon>
                </Tooltip>
                <div className="ml-12 leading-6">
                  {profile?.placeOfWork?.topName} ({profile?.placeOfWork?.top})
                </div>
              </div>
            )}
            <div className="flex items-center">
              <Tooltip title={t('Subdivision')} arrow placement="top">
                <FuseSvgIcon>react-outline:office-building</FuseSvgIcon>
              </Tooltip>
              <div className="ml-12 leading-6 text-black dark:text-white">
                {profile?.placeOfWork?.department}
              </div>
              {buildId && (
                <Link
                  to={`/apps/map/${buildId}`}
                  onClick={handleDispatchFloor}
                  className="flex items-center ml-12"
                  style={{ textDecoration: 'none' }}
                >
                  <FuseSvgIcon className="w-28 h-28 text-darker dark:text-white">
                    react-outline:map-pin
                  </FuseSvgIcon>
                </Link>
              )}
            </div>
            <div className="flex items-center">
              <Tooltip title={t('Position')} arrow placement="top">
                <FuseSvgIcon>react-outline:identification</FuseSvgIcon>
              </Tooltip>
              <div className="ml-12 leading-6">{profile?.placeOfWork?.position}</div>
            </div>
            {profile?.staffSkills?.length > 0 && (
              <div>
                <div className="flex items-center gap-12">
                  <FuseSvgIcon>material-outline:auto_fix_high</FuseSvgIcon>
                  <p>{t('Skills')}:</p>
                </div>
                <div className="flex flex-row flex-wrap items-center justify-start gap-12 mt-8 ml-10">
                  {profile?.staffSkills &&
                    profile.staffSkills.map((skill) => (
                      <div
                        key={skill.skillId}
                        className="flex flex-col justify-center items-center min-w-[60px]"
                      >
                        <img
                          src={getAbsoluteFileUrl(skill.pictureUrl)}
                          alt={skill.spSkillId}
                          className="object-fill w-32 h-32 mb-4"
                        />
                        <span className="text-center text-10">{skill.name}</span>
                      </div>
                    ))}
                </div>
              </div>
            )}
            {profile?.projects?.length > 0 && (
              <div className="flex items-start">
                <FuseSvgIcon>react-outline:view-boards</FuseSvgIcon>
                <div className="mt-2 ml-12">
                  <span className="font-600">{t('ParticipationInProjects')}:</span>
                  <ul className="ml-16">
                    {profile?.projects?.map((project) => (
                      <li
                        key={project.id}
                        onClick={() => navigateToProject(project)}
                        className={`px-4 list-disc mb-3 ${isTop && 'cursor-pointer'}`}
                      >
                        {project.title}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            )}
            {isTop && (
              <div className="flex flex-wrap gap-10">
                <Button
                  color="secondary"
                  variant="contained"
                  startIcon={<FuseSvgIcon>react-outline:view-boards</FuseSvgIcon>}
                  onClick={navigateToWorkload}
                >
                  {t('WorkloadByTasks')}
                </Button>
                <Button
                  color="secondary"
                  variant="contained"
                  startIcon={<FuseSvgIcon>react-outline:calendar</FuseSvgIcon>}
                  onClick={navigateToAttendances}
                >
                  {t('Attendance')}
                </Button>
                {isHr && (
                  <Button
                    color="secondary"
                    variant="contained"
                    startIcon={<FuseSvgIcon>react-outline:document</FuseSvgIcon>}
                    onClick={navigateToDocument}
                  >
                    {t('Documents')}
                  </Button>
                )}
              </div>
            )}
            <Divider className="my-8" />
            <div className="flex items-center">
              <FuseSvgIcon>react-outline:cake</FuseSvgIcon>
              <div className="ml-12 leading-6">
                {profile?.passport
                  ? moment(profile.passport.birthDate).format('DD.MM.YYYY')
                  : t('NotSpecified')}
              </div>
            </div>
            <div className="flex items-center">
              <FuseSvgIcon>react-outline:phone</FuseSvgIcon>
              <div className="ml-12 leading-6">
                {[...new Set([profile?.contact?.phoneMain, profile?.contact?.phoneAdditional])]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            </div>
            <div className="flex items-center">
              <FuseSvgIcon>react-outline:mail</FuseSvgIcon>
              <div className="ml-12 leading-6">
                {[
                  profile?.contact?.eMail?.toLowerCase(),
                  profile?.contact?.eMailAdditional?.toLowerCase(),
                ]
                  .filter(Boolean)
                  .join(', ')}
              </div>
            </div>
            {profile?.applicationUserId &&
              profile?.id > 0 &&
              user?.staffId > 0 &&
              user?.staffId !== id && (
                <div className="flex items-center">
                  <Button
                    className="ml-0"
                    color="secondary"
                    variant="contained"
                    startIcon={<FuseSvgIcon>react-outline:chat-alt</FuseSvgIcon>}
                    onClick={navigateToChat}
                  >
                    {t('GoToEmployeeChat')}
                  </Button>
                </div>
              )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default StaffContactDetails;
